import { Ban, ChevronRight, Folder, Loader2 } from "lucide-react";
import { useEffect, useRef, useState, type PointerEvent } from "react";
import { cn } from "@repo/utils";
import { Button } from "../../../button/button";
import type { DirectoryColumn } from "../types";

const DEFAULT_COLUMN_WIDTH = 224;
const MIN_COLUMN_WIDTH = 140;
const MAX_COLUMN_WIDTH = 480;

type Props = {
  columns: DirectoryColumn[];
  isLoading: boolean;
  error?: string;
  onFolderClick: (columnIndex: number, folderName: string) => void;
};

export const MacOSDirectoryList = ({ columns, isLoading, error, onFolderClick }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ index: number; startX: number; startWidth: number } | null>(null);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  useEffect(() => {
    const el = containerRef.current;
    if (el) el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
  }, [columns.length, isLoading]);

  const getWidth = (index: number) => columnWidths[index] ?? DEFAULT_COLUMN_WIDTH;

  const setWidth = (index: number, width: number) =>
    setColumnWidths((prev) => {
      const next = [...prev];
      next[index] = width;
      return next;
    });

  const handlePointerDown = (index: number) => (e: PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { index, startX: e.clientX, startWidth: getWidth(index) };
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const width = drag.startWidth + e.clientX - drag.startX;
    setWidth(drag.index, Math.min(MAX_COLUMN_WIDTH, Math.max(MIN_COLUMN_WIDTH, width)));
  };

  const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-red-400">
        <Ban className="h-6 w-6" />
        <span className="text-[12px] whitespace-pre-line">{error}</span>
      </div>
    );
  }

  if (isLoading && columns.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="text-[12px]">Loading folders...</span>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-1 overflow-x-auto overflow-y-hidden">
      {columns.map((column, columnIndex) => {
        const selectedName = columns[columnIndex + 1]?.path.split("/").pop();
        return (
          <div
            key={column.path}
            className="relative shrink-0 border-r border-gray-200"
            style={{ width: getWidth(columnIndex) }}
          >
            <ul className="h-full overflow-x-hidden overflow-y-auto py-1">
              {column.error && (
                <li className="px-3 py-2 text-[12px] text-red-400">{column.error}</li>
              )}
              {!column.error && column.folders.length === 0 && (
                <li className="px-3 py-2 text-[12px] text-gray-400 italic">No subfolders</li>
              )}
              {column.folders.map((folder) => {
                const isSelected = folder === selectedName;
                const isActive = isSelected && columnIndex === columns.length - 2;
                return (
                  <li key={folder} className="px-0.25">
                    <Button
                      variant="ghost"
                      size="sm"
                      fullWidth
                      justify="start"
                      title={folder}
                      onClick={() => onFolderClick(columnIndex, folder)}
                      className={cn(
                        "h-7 gap-2 rounded-md font-normal text-gray-700 focus-visible:ring-offset-0 focus-visible:ring-inset",
                        isSelected && "bg-gray-200",
                        isActive && "bg-blue-500 text-white hover:bg-blue-500 active:bg-blue-600"
                      )}
                    >
                      <Folder
                        className={cn(
                          "h-4 w-4 shrink-0 fill-blue-100 text-blue-500",
                          isActive && "fill-white/30 text-white"
                        )}
                      />
                      <span className="flex-1 truncate text-left">{folder}</span>
                      <ChevronRight className="h-3 w-3 shrink-0 opacity-60" />
                    </Button>
                  </li>
                );
              })}
            </ul>
            <div
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={getWidth(columnIndex)}
              aria-valuemin={MIN_COLUMN_WIDTH}
              aria-valuemax={MAX_COLUMN_WIDTH}
              title="Drag to resize, double-click to reset"
              onPointerDown={handlePointerDown(columnIndex)}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onDoubleClick={() => setWidth(columnIndex, DEFAULT_COLUMN_WIDTH)}
              className="absolute top-0 -right-1 z-10 h-full w-2 cursor-col-resize touch-none select-none hover:bg-blue-400/40 active:bg-blue-500/50"
            />
          </div>
        );
      })}
      {isLoading && (
        <div
          className="flex shrink-0 items-center justify-center text-gray-400"
          style={{ width: DEFAULT_COLUMN_WIDTH }}
        >
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      )}
    </div>
  );
};
